import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  DragOverlay,
  closestCorners,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getTodayEastern } from '../../lib/dateHelpers'
import { getJobScopeDisplay } from '../../lib/jobScopeDisplay'
import { isCancelled, SCHEDULABLE_STATUSES } from '../../utils/jobStatus'
import JobCard from '../jobs/JobCard'
import JobDetailErrorBoundary from '../jobs/JobDetailErrorBoundary'

const UNSCHEDULE_ZONE_ID = 'unschedule-zone'
const CELL_PREFIX = 'cell|'
const LS_CREW4 = 'crewBoard.showCrew4'
// 'in_progress' used to appear here but is a time_status value, never a jobs.status.
const ACTIVE_STATUSES_FILTER = SCHEDULABLE_STATUSES

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

function addDaysISO(iso, n) {
  // Use noon to dodge DST edges; we only care about the date part.
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

function fmtDayLabel(iso) {
  const d = new Date(iso + 'T12:00:00')
  return DAY_LABELS[d.getDay()] + ' ' + (d.getMonth() + 1) + '/' + d.getDate()
}

function fmtRange(startIso, days) {
  const a = new Date(startIso + 'T12:00:00')
  const b = new Date(addDaysISO(startIso, days - 1) + 'T12:00:00')
  const m = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return DAY_LABELS[a.getDay()] + ' ' + m(a) + ' – ' + DAY_LABELS[b.getDay()] + ' ' + m(b)
}

function cellId(date, crew) { return CELL_PREFIX + date + '|' + crew }
function parseCellId(id) {
  if (!id || !id.startsWith(CELL_PREFIX)) return null
  const parts = id.slice(CELL_PREFIX.length).split('|')
  if (parts.length !== 2) return null
  return { date: parts[0], crewNumber: parseInt(parts[1], 10) }
}

class BoardErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null } }
  static getDerivedStateFromError(error) { return { hasError: true, error } }
  componentDidCatch(error, info) { console.error('CrewScheduleBoard crash:', error, info) }
  render() {
    if (!this.state.hasError) return this.props.children
    const msg = (this.state.error && this.state.error.message) || 'Something went wrong on the schedule board.'
    return (
      <div style={{ padding: '40px', color: '#e05c5c', fontFamily: "'Barlow', sans-serif" }}>
        <div style={{ fontSize: '18px', marginBottom: '12px' }}>Schedule board failed to render</div>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{msg}</div>
        <button
          onClick={() => this.setState({ hasError: false, error: null })}
          style={{ marginTop: '16px', padding: '8px 14px', background: 'rgba(180,140,60,0.2)', border: '1px solid #b48c3c', color: '#b48c3c', borderRadius: '3px', cursor: 'pointer' }}
        >TRY AGAIN</button>
      </div>
    )
  }
}

export default function CrewScheduleBoardPage() {
  return (
    <BoardErrorBoundary>
      <Inner />
    </BoardErrorBoundary>
  )
}

function Inner() {
  const { profile, activeBusiness, setActiveBusiness } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const role = profile?.role
  const canManage = ['owner', 'office_staff', 'project_manager'].includes(role)

  const initialStart = useMemo(() => {
    const fromUrl = searchParams.get('start')
    if (fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) return fromUrl
    return getTodayEastern()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [windowStart, setWindowStart] = useState(initialStart)
  const [showCrew4, setShowCrew4] = useState(() => {
    try { return localStorage.getItem(LS_CREW4) === '1' } catch (e) { return false }
  })
  const [assignments, setAssignments] = useState([]) // job_crew_schedule rows for window
  const [jobs, setJobs] = useState([]) // jobs referenced by assignments OR sidebar
  const [crewMeta, setCrewMeta] = useState({}) // { 1: { name, count }, ... } keyed by crew_number for windowStart
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState(null)
  const [activeDragId, setActiveDragId] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const dragOriginRef = useRef(null) // snapshot for rollback

  const crewCount = showCrew4 ? 4 : 3
  const days = useMemo(() => [windowStart, addDaysISO(windowStart, 1), addDaysISO(windowStart, 2)], [windowStart])

  // Keep URL in sync.
  useEffect(() => {
    const cur = searchParams.get('start')
    if (cur !== windowStart) {
      const next = new URLSearchParams(searchParams)
      next.set('start', windowStart)
      setSearchParams(next, { replace: true })
    }
  }, [windowStart, searchParams, setSearchParams])

  // Persist crew 4 toggle.
  useEffect(() => {
    try { localStorage.setItem(LS_CREW4, showCrew4 ? '1' : '0') } catch (e) {}
  }, [showCrew4])

  const showToast = (msg, kind) => {
    setToast({ msg, kind: kind || 'info' })
    setTimeout(() => setToast(null), 4000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const winStart = days[0]
      const winEnd = days[days.length - 1]

      // 1. Assignments in window.
      const { data: jcsRows, error: jcsErr } = await supabase
        .from('job_crew_schedule')
        .select('id, job_id, crew_number, scheduled_date, sort_position')
        .gte('scheduled_date', winStart)
        .lte('scheduled_date', winEnd)
        .order('sort_position', { ascending: true })
      if (jcsErr) throw jcsErr

      // 2. Build the full job set we need: scheduled job ids + sidebar candidates.
      const scheduledJobIds = new Set((jcsRows ?? []).map((r) => r.job_id))

      let sidebarQuery = supabase
        .from('jobs')
        .select('id, job_number, business_id, status, job_type, title, client_name, client_phone, site_address, site_city, site_state, pool_size, pool_dimensions, pool_type, pool_install_type, is_tdi, in_place_of_old_pool, has_deep_end, dirt_haul_away, water_delivery, sand_delivery, start_date, sort_position, bid_id')
        .in('status', ACTIVE_STATUSES_FILTER)
      if (activeBusiness && activeBusiness !== 'all') sidebarQuery = sidebarQuery.eq('business_id', activeBusiness)
      const { data: sidebarJobs, error: sidebarErr } = await sidebarQuery
      if (sidebarErr) throw sidebarErr

      // Make sure every scheduled job is loaded too (even if its status would normally exclude it).
      const haveIds = new Set((sidebarJobs ?? []).map((j) => j.id))
      const missingIds = Array.from(scheduledJobIds).filter((id) => !haveIds.has(id))
      let extraJobs = []
      if (missingIds.length > 0) {
        const { data: ej, error: ejErr } = await supabase
          .from('jobs')
          .select('id, job_number, business_id, status, job_type, title, client_name, client_phone, site_address, site_city, site_state, pool_size, pool_dimensions, pool_type, pool_install_type, is_tdi, in_place_of_old_pool, has_deep_end, dirt_haul_away, water_delivery, sand_delivery, start_date, sort_position, bid_id')
          .in('id', missingIds)
        if (ejErr) throw ejErr
        extraJobs = ej ?? []
      }
      const allJobs = [...(sidebarJobs ?? []), ...extraJobs]

      // 3. Crew header meta — pull daily_crew_assignments for windowStart.
      const { data: dca } = await supabase
        .from('daily_crew_assignments')
        .select('crew_leader_id, crew_number, worker_id')
        .eq('work_date', winStart)
      const meta = {}
      const leaderIds = new Set()
      ;(dca ?? []).forEach((row) => {
        if (!row.crew_number) return
        const k = row.crew_number
        if (!meta[k]) meta[k] = { leaderId: row.crew_leader_id, count: 0 }
        meta[k].count += 1
        if (row.crew_leader_id) leaderIds.add(row.crew_leader_id)
      })
      if (leaderIds.size > 0) {
        const { data: leaderRows } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', Array.from(leaderIds))
        const nameById = {}
        ;(leaderRows ?? []).forEach((p) => { nameById[p.id] = p.full_name })
        Object.keys(meta).forEach((k) => {
          meta[k].name = nameById[meta[k].leaderId] || null
        })
      }

      setAssignments(jcsRows ?? [])
      setJobs(allJobs)
      setCrewMeta(meta)
    } catch (err) {
      console.error('Schedule board load error', err)
      showToast('Could not load schedule: ' + (err?.message || 'unknown'), 'error')
    } finally {
      setLoading(false)
    }
  }, [days, activeBusiness])

  useEffect(() => { load() }, [load])

  const jobMap = useMemo(() => {
    const m = new Map()
    jobs.forEach((j) => m.set(j.id, j))
    return m
  }, [jobs])

  // Index assignments by cell (date|crewNumber).
  const cellAssignments = useMemo(() => {
    const buckets = {}
    for (const d of days) {
      for (let c = 1; c <= 4; c++) {
        buckets[cellId(d, c)] = []
      }
    }
    assignments.forEach((a) => {
      const id = cellId(a.scheduled_date, a.crew_number)
      if (buckets[id]) buckets[id].push(a)
    })
    Object.keys(buckets).forEach((k) => buckets[k].sort((x, y) => x.sort_position - y.sort_position))
    return buckets
  }, [assignments, days])

  // Sidebar = jobs that don't appear in any visible-window cell.
  const sidebarJobs = useMemo(() => {
    const inWindow = new Set(assignments.map((a) => a.job_id))
    return jobs
      .filter((j) => !inWindow.has(j.id))
      .filter((j) => !j.status || ACTIVE_STATUSES_FILTER.includes(j.status))
      .sort((a, b) => {
        const ad = a.start_date || ''
        const bd = b.start_date || ''
        if (ad === bd) return (a.sort_position ?? 0) - (b.sort_position ?? 0)
        if (!ad) return -1
        if (!bd) return 1
        return ad.localeCompare(bd)
      })
  }, [jobs, assignments])

  // Locate a job's container by job_id (for drag resolution).
  const containerOfJob = useCallback((jobId) => {
    const a = assignments.find((x) => x.job_id === jobId)
    if (a) return cellId(a.scheduled_date, a.crew_number)
    return UNSCHEDULE_ZONE_ID
  }, [assignments])

  // Is the currently dragging card a scheduled job (lives in a cell)?
  const isDraggingScheduled = !!activeDragId && containerOfJob(activeDragId).startsWith(CELL_PREFIX)

  // ───────────────────────── DnD ─────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const onDragStart = (e) => {
    if (!canManage) return
    setActiveDragId(e.active?.id || null)
    dragOriginRef.current = {
      assignments: assignments.slice(),
    }
  }

  const resolveContainer = useCallback((overId) => {
    if (!overId) return null
    if (overId === UNSCHEDULE_ZONE_ID) return UNSCHEDULE_ZONE_ID
    if (typeof overId === 'string' && overId.startsWith(CELL_PREFIX)) return overId
    // Otherwise it's a job id — look up its container.
    return containerOfJob(overId)
  }, [containerOfJob])

  const onDragEnd = async (event) => {
    setActiveDragId(null)
    if (!canManage) return
    const { active, over } = event
    if (!over || !active) return
    const activeJobId = active.id
    const overContainer = resolveContainer(over.id)
    const fromContainer = containerOfJob(activeJobId)
    if (!overContainer || !fromContainer) return

    // No-op: dropped on its own card / its own container
    if (over.id === activeJobId) return

    const snapshotAssignments = assignments.slice()

    try {
      // Case A — drop on unschedule zone (sidebar) from a cell: delete assignment.
      if (overContainer === UNSCHEDULE_ZONE_ID && fromContainer.startsWith(CELL_PREFIX)) {
        const row = assignments.find((a) => a.job_id === activeJobId)
        if (!row) return
        // Optimistic: remove row.
        setAssignments((prev) => prev.filter((a) => a.id !== row.id))
        const { error } = await supabase.from('job_crew_schedule').delete().eq('id', row.id)
        if (error) throw error
        showToast('Removed from schedule', 'success')
        return
      }

      // Case B — already-unscheduled card dropped on the zone: no-op, no DB write.
      if (fromContainer === UNSCHEDULE_ZONE_ID && overContainer === UNSCHEDULE_ZONE_ID) return

      // Case C — sidebar → cell: insert.
      if (fromContainer === UNSCHEDULE_ZONE_ID && overContainer.startsWith(CELL_PREFIX)) {
        const target = parseCellId(overContainer)
        if (!target) return
        const cellRows = assignments.filter((a) => a.scheduled_date === target.date && a.crew_number === target.crewNumber)
        const nextPos = cellRows.length > 0 ? Math.max.apply(null, cellRows.map((r) => r.sort_position)) + 1 : 0
        // If `over.id` is a card in the cell (not the cell itself), insert at that index.
        let insertIndex = cellRows.length
        if (over.id !== overContainer) {
          const idx = cellRows.findIndex((r) => r.job_id === over.id)
          if (idx >= 0) insertIndex = idx
        }
        const tempId = 'temp-' + Math.random().toString(36).slice(2)
        const optimistic = {
          id: tempId,
          job_id: activeJobId,
          crew_number: target.crewNumber,
          scheduled_date: target.date,
          sort_position: nextPos,
        }
        setAssignments((prev) => [...prev, optimistic])
        const { data, error } = await supabase
          .from('job_crew_schedule')
          .insert({
            job_id: activeJobId,
            crew_number: target.crewNumber,
            scheduled_date: target.date,
            sort_position: nextPos,
          })
          .select()
          .single()
        if (error) throw error
        // Replace temp row with real one, then optionally renumber if user dropped between cards.
        setAssignments((prev) => prev.map((a) => a.id === tempId ? { ...optimistic, id: data.id, sort_position: data.sort_position } : a))
        if (insertIndex !== cellRows.length) {
          await renumberCell(target.date, target.crewNumber, [
            ...cellRows.slice(0, insertIndex).map((r) => r.job_id),
            activeJobId,
            ...cellRows.slice(insertIndex).map((r) => r.job_id),
          ])
        }
        showToast('Scheduled', 'success')
        return
      }

      // Case D — cell → cell.
      if (fromContainer.startsWith(CELL_PREFIX) && overContainer.startsWith(CELL_PREFIX)) {
        const target = parseCellId(overContainer)
        const source = parseCellId(fromContainer)
        if (!target || !source) return
        const row = assignments.find((a) => a.job_id === activeJobId)
        if (!row) return

        if (target.date === source.date && target.crewNumber === source.crewNumber) {
          // Reorder within cell.
          const cellRows = assignments.filter((a) => a.scheduled_date === target.date && a.crew_number === target.crewNumber).sort((x, y) => x.sort_position - y.sort_position)
          const oldIdx = cellRows.findIndex((r) => r.job_id === activeJobId)
          let newIdx = cellRows.findIndex((r) => r.job_id === over.id)
          if (newIdx < 0) newIdx = cellRows.length - 1
          if (oldIdx === newIdx) return
          const reordered = arrayMove(cellRows, oldIdx, newIdx)
          // Optimistic.
          setAssignments((prev) => prev.map((a) => {
            const idx = reordered.findIndex((r) => r.id === a.id)
            return idx >= 0 ? { ...a, sort_position: idx } : a
          }))
          await renumberCell(target.date, target.crewNumber, reordered.map((r) => r.job_id))
          return
        }

        // Cross-cell move: update row, then recompute sort positions in destination.
        const destRows = assignments.filter((a) => a.scheduled_date === target.date && a.crew_number === target.crewNumber).sort((x, y) => x.sort_position - y.sort_position)
        let destIndex = destRows.length
        if (over.id !== overContainer) {
          const idx = destRows.findIndex((r) => r.job_id === over.id)
          if (idx >= 0) destIndex = idx
        }
        // Optimistic: move the row.
        setAssignments((prev) => prev.map((a) => a.id === row.id ? { ...a, scheduled_date: target.date, crew_number: target.crewNumber, sort_position: destIndex } : a))
        const { error } = await supabase
          .from('job_crew_schedule')
          .update({
            scheduled_date: target.date,
            crew_number: target.crewNumber,
            sort_position: destIndex,
          })
          .eq('id', row.id)
        if (error) throw error
        const newDestOrder = [
          ...destRows.slice(0, destIndex).map((r) => r.job_id),
          activeJobId,
          ...destRows.slice(destIndex).map((r) => r.job_id),
        ]
        await renumberCell(target.date, target.crewNumber, newDestOrder)
        showToast('Moved', 'success')
        return
      }
    } catch (err) {
      console.error('Drag persist failed', err)
      // Rollback to snapshot.
      setAssignments(snapshotAssignments)
      // Special case: unique constraint violation.
      if (err?.code === '23505') {
        showToast('That job is already scheduled for that crew/day.', 'error')
      } else {
        showToast('Save failed: ' + (err?.message || 'unknown'), 'error')
      }
    }
  }

  // Persist a new sort order for a cell. Best-effort — failures roll back via caller's snapshot.
  const renumberCell = async (date, crewNumber, orderedJobIds) => {
    const cellRows = assignments.filter((a) => a.scheduled_date === date && a.crew_number === crewNumber)
    const idsByJob = new Map(cellRows.map((r) => [r.job_id, r.id]))
    const updates = orderedJobIds.map((jobId, idx) => {
      const id = idsByJob.get(jobId)
      if (!id) return null
      return { id, idx }
    }).filter(Boolean)
    // Update in parallel — small lists.
    await Promise.all(updates.map(({ id, idx }) =>
      supabase.from('job_crew_schedule').update({ sort_position: idx }).eq('id', id)
    ))
    setAssignments((prev) => prev.map((a) => {
      const m = updates.find((u) => u.id === a.id)
      return m ? { ...a, sort_position: m.idx } : a
    }))
  }

  // ───────────────────────── header actions ─────────────────────────

  const shiftWindow = (n) => setWindowStart((s) => addDaysISO(s, n))
  const todayWindow = () => setWindowStart(getTodayEastern())

  const onCardClick = (jobId) => {
    const j = jobMap.get(jobId)
    if (j) setSelectedJob(j)
  }

  // ───────────────────────── render ─────────────────────────

  const accessOk = canManage || role === 'crew_leader'

  if (!profile) return null
  if (!accessOk) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>SCHEDULE BOARD</div>
        <div style={{ marginTop: '20px', color: 'rgba(255,255,255,0.5)' }}>You don’t have access to this page.</div>
      </div>
    )
  }

  const crew4Rows = assignments.filter((a) => a.crew_number === 4)
  const showHideCrew4 = showCrew4 && crew4Rows.length === 0

  return (
    <div style={styles.container}>
      {/* Sticky header */}
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <div style={styles.title}>CREW SCHEDULE BOARD</div>
          <div style={styles.subtitle}>{fmtRange(windowStart, 3)}</div>
        </div>

        <div style={styles.headerControls}>
          {/* Business filter pills */}
          <div style={styles.pillRow}>
            {['all', 'pools', 'riverbend'].map((b) => (
              <button
                key={b}
                onClick={() => setActiveBusiness(b)}
                style={{ ...styles.pill, ...(activeBusiness === b ? styles.pillActive : {}) }}
              >{b === 'all' ? 'ALL' : b === 'pools' ? 'POOLS' : 'RIVERBEND'}</button>
            ))}
          </div>

          <div style={styles.dateNav}>
            <button onClick={() => shiftWindow(-3)} style={styles.navBtn} title="Previous 3 days">◀</button>
            <input
              type="date"
              value={windowStart}
              onChange={(e) => { if (e.target.value) setWindowStart(e.target.value) }}
              style={styles.datePicker}
            />
            <button onClick={() => shiftWindow(3)} style={styles.navBtn} title="Next 3 days">▶</button>
            <button onClick={todayWindow} style={styles.todayBtn}>TODAY</button>
          </div>

          {canManage && (
            <div style={styles.crew4Btn}>
              {!showCrew4 && (
                <button onClick={() => setShowCrew4(true)} style={styles.addBtn}>+ ADD 4TH CREW</button>
              )}
              {showHideCrew4 && (
                <button onClick={() => setShowCrew4(false)} style={styles.hideBtn}>HIDE CREW 4</button>
              )}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div style={{
          ...styles.toast,
          backgroundColor: toast.kind === 'error' ? 'rgba(224,92,92,0.15)' : toast.kind === 'success' ? 'rgba(74,140,106,0.15)' : 'rgba(180,140,60,0.15)',
          borderColor: toast.kind === 'error' ? 'rgba(224,92,92,0.4)' : toast.kind === 'success' ? 'rgba(74,140,106,0.4)' : 'rgba(180,140,60,0.4)',
          color: toast.kind === 'error' ? '#e05c5c' : toast.kind === 'success' ? '#6fbf8e' : '#b48c3c',
        }}>{toast.msg}</div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div style={styles.layout}>
          {/* Board */}
          <div style={styles.board}>
            {/* Crew column headers */}
            <div style={{ ...styles.boardRow, ...styles.crewHeaderRow, gridTemplateColumns: 'repeat(' + crewCount + ', minmax(0, 1fr))' }}>
              {Array.from({ length: crewCount }, (_, i) => i + 1).map((crew) => {
                const m = crewMeta[crew]
                return (
                  <div key={crew} style={styles.crewHeader}>
                    <div style={styles.crewHeaderTop}>
                      <div style={styles.crewHeaderLabel}>CREW {crew}</div>
                      {crew === 4 && showHideCrew4 && (
                        <button onClick={() => setShowCrew4(false)} style={styles.inlineHide}>hide</button>
                      )}
                    </div>
                    <div style={styles.crewHeaderMeta}>
                      {m?.name ? (m.name + ' · ' + m.count + (m.count === 1 ? ' worker' : ' workers')) : 'No crew set'}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Days × crew grid */}
            {days.map((d) => (
              <div key={d} style={{ ...styles.boardRow, gridTemplateColumns: 'repeat(' + crewCount + ', minmax(0, 1fr))' }}>
                {Array.from({ length: crewCount }, (_, i) => i + 1).map((crew, crewIdx) => {
                  const id = cellId(d, crew)
                  const rows = cellAssignments[id] || []
                  return (
                    <CrewDayCell
                      key={id}
                      cellKey={id}
                      date={d}
                      crewNumber={crew}
                      showDayLabel={crewIdx === 0}
                      rows={rows}
                      jobMap={jobMap}
                      canManage={canManage}
                      onCardClick={onCardClick}
                    />
                  )
                })}
              </div>
            ))}
            {loading && <div style={styles.loading}>Loading…</div>}
          </div>

          {/* Sidebar */}
          <UpNextSidebar
            jobs={sidebarJobs}
            onCardClick={onCardClick}
            canManage={canManage}
            isDraggingScheduled={isDraggingScheduled}
          />
        </div>

        <DragOverlay>
          {activeDragId ? <DraggedJobPreview job={jobMap.get(activeDragId)} /> : null}
        </DragOverlay>
      </DndContext>

      {selectedJob && (
        <JobDetailErrorBoundary key={selectedJob.id} job={selectedJob} onClose={() => setSelectedJob(null)}>
          <JobCard job={selectedJob} onClose={() => setSelectedJob(null)} onUpdate={() => { load(); setSelectedJob(null) }} />
        </JobDetailErrorBoundary>
      )}
    </div>
  )
}

// ───────────────────── Cell ─────────────────────

function CrewDayCell({ cellKey, date, crewNumber, showDayLabel, rows, jobMap, canManage, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: cellKey, disabled: !canManage })
  const items = rows.map((r) => r.job_id)
  return (
    <div ref={setNodeRef} style={{ ...styles.cell, ...(isOver ? styles.cellOver : {}) }}>
      {showDayLabel && <div style={styles.cellDayLabel}>{fmtDayLabel(date)}</div>}
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div style={styles.cellList}>
          {rows.map((r) => {
            const j = jobMap.get(r.job_id)
            if (!j) return null
            return (
              <SortableJobCard
                key={r.id}
                jobId={r.job_id}
                job={j}
                onClick={() => onCardClick(r.job_id)}
                canManage={canManage}
              />
            )
          })}
          {rows.length === 0 && (
            <div style={styles.cellEmpty}>{canManage ? 'Drop a job here' : '—'}</div>
          )}
        </div>
      </SortableContext>
    </div>
  )
}

// ───────────────────── Sidebar ─────────────────────

function UpNextSidebar({ jobs, onCardClick, canManage, isDraggingScheduled }) {
  const { setNodeRef, isOver } = useDroppable({ id: UNSCHEDULE_ZONE_ID, disabled: !canManage })
  // Light up the pill only when isOver AND the dragging card is currently scheduled.
  const pillActive = isOver && isDraggingScheduled
  return (
    <aside ref={setNodeRef} style={{ ...styles.sidebar, ...(pillActive ? styles.sidebarHotEdge : {}) }}>
      <div style={styles.sidebarHeader}>
        <div style={styles.sidebarTitle}>UP NEXT <span style={styles.sidebarCount}>({jobs.length})</span></div>
      </div>
      <div style={{ ...styles.removeZone, ...(pillActive ? styles.removeZoneActive : {}) }}>
        {pillActive ? 'Drop here to unschedule' : 'Drop here to unschedule'}
      </div>
      <div style={styles.sidebarList}>
        {jobs.map((j) => (
          <DraggableJobCard
            key={j.id}
            jobId={j.id}
            job={j}
            onClick={() => onCardClick(j.id)}
            canManage={canManage}
          />
        ))}
        {jobs.length === 0 && <div style={styles.sidebarEmpty}>No unscheduled jobs.</div>}
      </div>
    </aside>
  )
}

// ───────────────────── Cards ─────────────────────

function CardBody({ job }) {
  if (!job) return null
  const { headline, subline, chips } = getJobScopeDisplay(job)
  // Jobs already placed on the board are loaded regardless of status, so a job
  // cancelled after scheduling stays in its cell. Mark it plainly instead of
  // letting it read as ordinary scheduled work.
  const cancelled = isCancelled(job)
  return (
    <>
      {cancelled && <span style={styles.cardCancelledBadge}>CANCELLED</span>}
      <div style={{ ...styles.cardHeadline, ...(cancelled ? styles.cardCancelledText : {}) }}>{headline}</div>
      {subline && <div style={{ ...styles.cardSubline, ...(cancelled ? styles.cardCancelledText : {}) }}>{subline}</div>}
      {chips.length > 0 && (
        <div style={styles.cardChips}>
          {chips.map((c) => (
            <span key={c.key} style={styles.cardChip}>{c.label}</span>
          ))}
        </div>
      )}
      {job?.job_number && <span style={styles.cardBadge}>{job.job_number}</span>}
    </>
  )
}

function SortableJobCard({ jobId, job, onClick, canManage }) {
  const sortable = useSortable({ id: jobId, disabled: !canManage })
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <div
      ref={setNodeRef}
      style={{ ...styles.card, ...style }}
      {...attributes}
      {...listeners}
      onClick={(e) => { e.stopPropagation(); onClick && onClick() }}
    >
      <CardBody job={job} />
    </div>
  )
}

function DraggableJobCard({ jobId, job, onClick, canManage }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: jobId, disabled: !canManage })
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  }
  return (
    <div
      ref={setNodeRef}
      style={{ ...styles.card, ...style }}
      {...attributes}
      {...listeners}
      onClick={(e) => { e.stopPropagation(); onClick && onClick() }}
    >
      <CardBody job={job} />
    </div>
  )
}

function DraggedJobPreview({ job }) {
  if (!job) return null
  return (
    <div style={{ ...styles.card, ...styles.cardDragOverlay }}>
      <CardBody job={job} />
    </div>
  )
}

// ───────────────────── Styles ─────────────────────

const styles = {
  container: { fontFamily: "'Barlow', sans-serif", color: '#ffffff', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', minHeight: '500px' },
  header: { position: 'sticky', top: 0, zIndex: 5, backgroundColor: '#0a0f1a', padding: '12px 0 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  titleRow: { display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '24px', fontWeight: 700, letterSpacing: '3px' },
  subtitle: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  headerControls: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' },
  pillRow: { display: 'flex', gap: '6px' },
  pill: { padding: '6px 12px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' },
  pillActive: { backgroundColor: 'rgba(180,140,60,0.18)', borderColor: '#b48c3c', color: '#b48c3c' },
  dateNav: { display: 'flex', alignItems: 'center', gap: '8px' },
  navBtn: { width: '32px', height: '32px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', cursor: 'pointer' },
  datePicker: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', padding: '6px 10px', fontFamily: "'Barlow', sans-serif", fontSize: '13px', colorScheme: 'dark' },
  todayBtn: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(180,140,60,0.4)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer' },
  crew4Btn: { marginLeft: 'auto' },
  addBtn: { padding: '8px 14px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.4)', borderRadius: '3px', color: '#6fbf8e', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer' },
  hideBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', cursor: 'pointer' },
  toast: { padding: '10px 14px', borderRadius: '3px', border: '1px solid', fontSize: '13px', marginTop: '8px' },

  layout: { display: 'flex', gap: '12px', flex: 1, minHeight: 0, marginTop: '12px' },
  board: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' },
  boardRow: { display: 'grid', gap: '8px' },
  crewHeaderRow: { position: 'sticky', top: 0, backgroundColor: '#0a0f1a', zIndex: 2, paddingBottom: '4px' },
  crewHeader: { padding: '8px 10px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px' },
  crewHeaderTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  crewHeaderLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  inlineHide: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' },
  crewHeaderMeta: { fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '4px' },

  cell: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', padding: '8px', minHeight: '120px', display: 'flex', flexDirection: 'column', gap: '6px' },
  cellOver: { borderColor: 'rgba(180,140,60,0.6)', backgroundColor: 'rgba(180,140,60,0.06)' },
  cellDayLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.5)' },
  cellList: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  cellEmpty: { fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic', padding: '12px 4px', textAlign: 'center' },

  card: { position: 'relative', backgroundColor: '#101725', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 10px', cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'manipulation' },
  cardDragOverlay: { boxShadow: '0 8px 30px rgba(0,0,0,0.6)', cursor: 'grabbing', borderColor: '#b48c3c' },
  cardHeadline: { fontWeight: 700, fontSize: '13px', lineHeight: 1.2, paddingRight: '60px' },
  cardSubline: { fontSize: '11px', color: 'rgba(255,255,255,0.55)', marginTop: '3px' },
  cardChips: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' },
  cardChip: { fontSize: '10px', lineHeight: 1.4, padding: '2px 6px', borderRadius: '8px', backgroundColor: 'rgba(180,140,60,0.15)', color: '#d2b674', border: '1px solid rgba(180,140,60,0.25)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.5px' },
  cardCancelledText: { textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)' },
  cardCancelledBadge: { display: 'inline-block', marginBottom: '4px', padding: '1px 5px', borderRadius: '3px', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1px', color: 'rgba(255,255,255,0.6)' },
  cardBadge: { position: 'absolute', top: '6px', right: '8px', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', letterSpacing: '1px', color: 'rgba(180,140,60,0.7)' },

  sidebar: { width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', transition: 'border-color 0.15s' },
  sidebarHotEdge: { borderColor: 'rgba(224,92,92,0.5)' },
  sidebarHeader: { padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  sidebarTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  sidebarCount: { color: 'rgba(255,255,255,0.4)', marginLeft: '6px' },
  removeZone: { margin: '8px', padding: '12px', textAlign: 'center', border: '1px dashed rgba(224,92,92,0.4)', borderRadius: '3px', color: 'rgba(224,92,92,0.7)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', letterSpacing: '2px' },
  removeZoneActive: { backgroundColor: 'rgba(224,92,92,0.12)', color: '#e05c5c', borderColor: '#e05c5c' },
  sidebarList: { flex: 1, overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  sidebarEmpty: { padding: '24px 12px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '12px' },

  loading: { padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
}
